import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import { useStaffQueue } from '../../hooks/useStaffQueue';
import { QueueList } from '../../components/admin/QueueList';
import ReconnectingBanner from '../../components/ReconnectingBanner';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

const statusColor = { connected: '#10B981', connecting: '#F59E0B', disconnected: '#EF4444' };

const styles = {
  dot: (status) => ({
    width: 8, height: 8, borderRadius: '50%', background: statusColor[status] || '#94A3B8',
    display: 'inline-block', marginRight: 6,
  }),
  statusText: { fontSize: '0.8rem', color: '#94A3B8' },
  topBar: {
    display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap',
  },
  deskCard: {
    background: '#1E293B', border: '1px solid #334155', borderRadius: 10,
    padding: '0.75rem 1rem', marginBottom: '1rem',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  deskLabel: { fontSize: '0.8rem', color: '#94A3B8', marginRight: 8 },
  deskValue: { fontWeight: 700, fontSize: '1rem', color: '#06B6D4' },
  deskEditBtn: {
    padding: '0.3rem 0.6rem', borderRadius: 6, border: '1.5px solid #334155',
    background: 'transparent', cursor: 'pointer', fontSize: '0.75rem', color: '#94A3B8',
  },
  deskInput: {
    padding: '0.4rem 0.6rem', borderRadius: 6, border: '1.5px solid #7C3AED',
    background: '#1E293B', color: '#F8FAFC', fontSize: '0.95rem', width: 140, fontWeight: 600,
  },
  deskActionBtn: {
    padding: '0.35rem 0.6rem', borderRadius: 6, border: 'none',
    background: '#7C3AED', cursor: 'pointer', fontSize: '0.75rem', color: '#fff', fontWeight: 600,
  },
  deskCancelBtn: {
    padding: '0.35rem 0.6rem', borderRadius: 6, border: '1.5px solid #334155',
    background: 'transparent', cursor: 'pointer', fontSize: '0.75rem', color: '#94A3B8',
  },
  emptyCard: {
    background: '#1E293B', borderRadius: 12, padding: '2rem', border: '1px solid #334155',
    textAlign: 'center',
  },
  queueControls: {
    background: '#1E293B', border: '1px solid #334155', borderRadius: 10,
    padding: '0.75rem 1rem', marginBottom: '1rem',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
    flexWrap: 'wrap',
  },
  switchRow: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  switchInput: { width: 18, height: 18, accentColor: '#7C3AED', cursor: 'pointer' },
  switchLabel: { fontSize: '0.9rem', fontWeight: 700 },
  closeDayBtn: (emptyQueue) => ({
    padding: '0.5rem 0.9rem', borderRadius: 8,
    border: '1px solid ' + (emptyQueue ? '#475569' : '#B91C1C'),
    background: emptyQueue ? 'transparent' : 'rgba(239,68,68,0.12)',
    color: emptyQueue ? '#94A3B8' : '#FCA5A5',
    fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
  }),
  modalBackdrop: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 400, padding: '1rem',
  },
  modalCard: {
    background: '#1E293B', border: '1px solid #334155', borderRadius: 14,
    padding: '1.5rem', maxWidth: 420, width: '100%',
  },
  modalTitle: { margin: '0 0 0.75rem', color: '#F8FAFC', fontSize: '1.1rem', fontWeight: 700 },
  modalBody: { margin: '0 0 1.25rem', color: '#CBD5E1', lineHeight: 1.5, fontSize: '0.9rem' },
  modalButtons: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' },
  modalCancel: {
    padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #334155',
    background: 'transparent', color: '#94A3B8', fontSize: '0.88rem', cursor: 'pointer',
  },
  modalConfirm: {
    padding: '0.5rem 1rem', borderRadius: 8, border: 'none',
    background: '#DC2626', color: '#fff', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
  },
  main: { maxWidth: 720, margin: '0 auto', padding: '1.5rem 1rem' },
  shopSelect: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155',
    fontSize: '1rem', marginBottom: '1.5rem', background: '#1E293B', color: '#F8FAFC',
  },
  serviceSection: {
    background: '#1E293B', borderRadius: 12, padding: '1rem 1.25rem',
    boxShadow: '0 2px 12px rgba(0,0,0,0.25)', marginBottom: '1rem', border: '1px solid #334155',
  },
  serviceHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem',
  },
  serviceName: { fontWeight: 700, fontSize: '0.95rem', color: '#F8FAFC' },
  serviceCount: { fontSize: '0.8rem', color: '#94A3B8' },
  nowServingCard: {
    background: 'linear-gradient(135deg, rgba(124,58,237,0.18), rgba(6,182,212,0.12))',
    border: '1px solid #7C3AED', borderRadius: 12, padding: '1rem 1.25rem',
    marginBottom: '1.25rem', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap',
  },
  nowServingLabel: {
    fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em',
    textTransform: 'uppercase', color: '#A78BFA', marginBottom: '0.25rem',
  },
  nowServingTicket: { fontWeight: 700, fontSize: '1.05rem', color: '#F8FAFC' },
  nowServingActions: { display: 'flex', gap: 8 },
  nsSkipBtn: {
    padding: '0.45rem 0.85rem', borderRadius: 6, border: '1.5px solid #F59E0B',
    background: 'transparent', color: '#FBBF24', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
  },
  nsDoneBtn: {
    padding: '0.45rem 0.85rem', borderRadius: 6, border: 'none',
    background: '#10B981', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
  },
  callBtn: {
    padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff', fontWeight: 700, fontSize: '0.85rem',
    cursor: 'pointer',
  },
  sectionTitle: { fontWeight: 600, color: '#94A3B8', fontSize: '0.85rem', marginBottom: '0.75rem' },
  chipBar: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: '1.25rem' },
  chip: (active) => ({
    padding: '0.45rem 0.9rem', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontWeight: 600, fontSize: '0.82rem',
    background: active ? '#7C3AED' : '#334155', color: active ? '#fff' : '#94A3B8',
    transition: 'background 0.15s, color 0.15s',
  }),
};

export default function StaffQueuePage() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [shops, setShops] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [myAssignments, setMyAssignments] = useState(null);
  const [activeServiceIds, setActiveServiceIds] = useState(new Set());

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API_BASE}/api/v1/shops`, { headers }).then((r) => r.json()),
      user?.role !== 'OWNER'
        ? fetch(`${API_BASE}/api/v1/shops/my-shops`, { headers }).then((r) => r.ok ? r.json() : [])
        : Promise.resolve(null),
    ]).then(([allShops, myShopAssignments]) => {
      var active = (allShops || []).filter(s => s.active !== false);
      if (myShopAssignments && myShopAssignments.length > 0) {
        const assignedIds = new Set(myShopAssignments.map(a => a.shopId));
        active = active.filter(s => assignedIds.has(s.id));
      }
      setShops(active);
      if (active.length > 0) setSelectedShopId(active[0].id);
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!selectedShopId) return;
    fetch(`${API_BASE}/api/v1/services/by-shop/${selectedShopId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setServices)
      .catch(() => setServices([]));
  }, [selectedShopId, token]);

  // Fetch staff's assigned services
  useEffect(() => {
    fetch(`${API_BASE}/api/v1/services/my-services`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        setMyAssignments(data);
        setActiveServiceIds(new Set(data.map((a) => a.serviceId)));
      })
      .catch(() => setMyAssignments([]));
  }, [token]);

  const [deskLabel, setDeskLabel] = useState('');
  const [deskEditing, setDeskEditing] = useState(false);
  const [deskDraft, setDeskDraft] = useState('');
  const [deskSaved, setDeskSaved] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/staff/desk-label`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : { deskLabel: '' })
      .then((data) => setDeskLabel(data.deskLabel || ''))
      .catch(() => {});
  }, [token]);

  const saveDeskLabel = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/staff/desk-label`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deskLabel: deskDraft }),
      });
      if (res.ok) {
        const data = await res.json();
        setDeskLabel(data.deskLabel || '');
        setDeskEditing(false);
        setDeskSaved(true);
        setTimeout(() => setDeskSaved(false), 2000);
      }
    } catch {}
  };

  const { queues, queue, calledTicket, wsStatus, retryAttempt, callNext, skipTicket, finishTicket, closeShopForToday } =
    useStaffQueue(selectedShopId, token);

  // "Accepting new customers" toggle + "Close for today" action live at the
  // shop level. Both take effect immediately for every employee and every
  // customer trying to join — there's no per-employee view.
  const [acceptingNew, setAcceptingNew]       = useState(true);
  const [acceptingBusy, setAcceptingBusy]     = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeBusy, setCloseBusy]             = useState(false);

  // Load the shop's current accepting flag and refresh it when the shop
  // changes. The /public/shops/by-id endpoint returns it in its payload.
  useEffect(() => {
    if (!selectedShopId) return;
    fetch(`${API_BASE}/api/v1/public/shops/by-id/${selectedShopId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d.acceptingNewCustomers === 'boolean') {
          setAcceptingNew(d.acceptingNewCustomers);
        }
      })
      .catch(() => {});
  }, [selectedShopId]);

  const toggleAccepting = async () => {
    if (!selectedShopId || acceptingBusy) return;
    const next = !acceptingNew;
    setAcceptingBusy(true);
    // Optimistic update — roll back on failure.
    setAcceptingNew(next);
    try {
      const res = await fetch(`${API_BASE}/api/v1/shops/${selectedShopId}/accepting-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ accepting: next }),
      });
      if (!res.ok) throw new Error('Toggle failed');
      const data = await res.json();
      setAcceptingNew(!!data.accepting);
    } catch {
      setAcceptingNew(!next); // rollback
    } finally {
      setAcceptingBusy(false);
    }
  };

  const doCloseForToday = async () => {
    if (closeBusy) return;
    setCloseBusy(true);
    try {
      await closeShopForToday();
      // The WS broadcast will clear the queue automatically; flip the flag
      // locally too so the UI is consistent before the toggle-flag round-trip.
      setAcceptingNew(false);
      setShowCloseConfirm(false);
    } finally {
      setCloseBusy(false);
    }
  };

  const totalWaiting = Object.values(queues).reduce((n, arr) => n + arr.length, 0);

  // Filter services: if staff has assignments, only show assigned; otherwise show all
  const isStaff = user?.role === 'STAFF';
  const hasAssignments = myAssignments && myAssignments.length > 0;
  const visibleServices = hasAssignments
    ? services.filter((svc) => myAssignments.some((a) => a.serviceId === svc.id))
    : services;
  const activeServices = visibleServices.filter((svc) => activeServiceIds.has(svc.id));
  const hasServices = visibleServices.length > 0;

  const toggleService = (svcId) => {
    setActiveServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(svcId)) next.delete(svcId); else next.add(svcId);
      return next;
    });
  };

  return (
    <div>
      {wsStatus !== 'connected' && selectedShopId && <ReconnectingBanner attempt={retryAttempt} />}
      <main style={styles.main}>
        {shops.length > 1 && (
          <select
            style={styles.shopSelect}
            value={selectedShopId}
            onChange={(e) => setSelectedShopId(e.target.value)}
          >
            {shops.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}

        <div style={styles.topBar}>
          <span style={styles.statusText}>
            <span style={styles.dot(wsStatus)} />
            {wsStatus === 'connected' ? t('staffQueue.connected') : wsStatus === 'connecting' ? t('staffQueue.connecting') : t('staffQueue.disconnected')}
          </span>
        </div>

        {/* Desk label card */}
        <div style={styles.deskCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={styles.deskLabel}>{t('staffQueue.myDesk')}:</span>
            {deskEditing ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  style={styles.deskInput}
                  value={deskDraft}
                  onChange={(e) => setDeskDraft(e.target.value)}
                  placeholder={t('staffQueue.deskPlaceholder')}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') saveDeskLabel(); if (e.key === 'Escape') setDeskEditing(false); }}
                />
                <button style={styles.deskActionBtn} onClick={saveDeskLabel}>{t('staffQueue.save')}</button>
                <button style={styles.deskCancelBtn} onClick={() => setDeskEditing(false)}>{t('staffQueue.cancel')}</button>
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={styles.deskValue}>{deskLabel || t('staffQueue.notSet')}</span>
                <button style={styles.deskEditBtn} onClick={() => { setDeskDraft(deskLabel); setDeskEditing(true); }}>{t('staffQueue.edit')}</button>
                {deskSaved && <span style={{ color: '#10B981', fontSize: '0.75rem' }}>{t('staffQueue.deskSaved')}</span>}
              </span>
            )}
          </div>
        </div>
        {/* Accepting new customers toggle + Close-for-today action.
            Shop-level — one click applies to every employee and every
            customer trying to join. Close-for-today has a confirmation
            step because it dismisses everyone currently waiting. */}
        {selectedShopId && (
          <div style={styles.queueControls}>
            <label style={styles.switchRow}>
              <input
                type="checkbox"
                checked={acceptingNew}
                onChange={toggleAccepting}
                disabled={acceptingBusy}
                style={styles.switchInput}
              />
              <span style={{
                ...styles.switchLabel,
                color: acceptingNew ? '#10B981' : '#F59E0B',
              }}>
                {acceptingNew
                  ? t('staffQueue.acceptingOn')
                  : t('staffQueue.acceptingOff')}
              </span>
            </label>
            <button
              type="button"
              style={styles.closeDayBtn(totalWaiting === 0)}
              disabled={closeBusy}
              onClick={() => setShowCloseConfirm(true)}
            >
              {t('staffQueue.closeForToday')}
            </button>
          </div>
        )}

        {/* Close-for-today confirmation */}
        {showCloseConfirm && (
          <div style={styles.modalBackdrop}>
            <div style={styles.modalCard}>
              <h3 style={styles.modalTitle}>{t('staffQueue.closeConfirmTitle')}</h3>
              <p style={styles.modalBody}>
                {totalWaiting > 0
                  ? t('staffQueue.closeConfirmBody', { count: totalWaiting })
                  : t('staffQueue.closeConfirmBodyEmpty')}
              </p>
              <div style={styles.modalButtons}>
                <button
                  type="button"
                  style={styles.modalCancel}
                  onClick={() => setShowCloseConfirm(false)}
                  disabled={closeBusy}
                >
                  {t('staffQueue.cancel')}
                </button>
                <button
                  type="button"
                  style={styles.modalConfirm}
                  onClick={doCloseForToday}
                  disabled={closeBusy}
                >
                  {closeBusy ? t('staffQueue.closing') : t('staffQueue.closeConfirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Currently-served customer. Call Next pops a ticket out of the
            waiting list, so without this card staff would have no way to
            complete or skip the customer they just called. */}
        {calledTicket && (
          <div style={styles.nowServingCard} data-testid="now-serving">
            <div>
              <div style={styles.nowServingLabel}>{t('staffQueue.nowServing')}</div>
              <div style={styles.nowServingTicket}>
                #{calledTicket.ticketNumber || '—'} &middot; {calledTicket.firstName || t('ticketCard.guest')}
              </div>
            </div>
            <div style={styles.nowServingActions}>
              <button
                type="button"
                style={styles.nsSkipBtn}
                onClick={() => skipTicket(calledTicket.ticketUuid, calledTicket.serviceId || '')}
              >
                {t('ticketCard.skipBtn')}
              </button>
              <button
                type="button"
                style={styles.nsDoneBtn}
                onClick={() => finishTicket(calledTicket.ticketUuid, calledTicket.serviceId || '')}
              >
                {t('ticketCard.doneBtn')}
              </button>
            </div>
          </div>
        )}

        {shops.length === 0 && (
          <div style={styles.emptyCard}>
            <p style={{ fontSize: '2rem', margin: '0 0 0.75rem' }}>🏪</p>
            <p style={{ color: '#F8FAFC', fontWeight: 600, fontSize: '1rem', margin: '0 0 0.5rem' }}>{t('staffQueue.noShops')}</p>
            <p style={{ color: '#94A3B8', fontSize: '0.85rem', margin: 0 }}>{t('staffQueue.noShopsSub')}</p>
          </div>
        )}

        {shops.length > 0 && hasServices ? (
          <>
            {/* Service toggle chips */}
            <div style={styles.chipBar}>
              {visibleServices.map((svc) => {
                const count = (queues[svc.id] || []).length;
                return (
                  <button key={svc.id} style={styles.chip(activeServiceIds.has(svc.id))} onClick={() => toggleService(svc.id)}>
                    {svc.name} ({count})
                  </button>
                );
              })}
            </div>
            {/* Queue sections for active services */}
            {activeServices.map((svc) => {
              const svcQueue = queues[svc.id] || [];
              return (
                <div key={svc.id} style={styles.serviceSection}>
                  <div style={styles.serviceHeader}>
                    <div>
                      <div style={styles.serviceName}>{svc.name}</div>
                      <div style={styles.serviceCount}>{svcQueue.length} {t('staffQueue.waiting')}</div>
                    </div>
                    <button
                      style={{ ...styles.callBtn, opacity: wsStatus !== 'connected' ? 0.5 : 1 }}
                      onClick={() => callNext(svc.id)}
                      disabled={wsStatus !== 'connected'}
                    >
                      {t('staffQueue.callNext')}
                    </button>
                  </div>
                  <QueueList
                    entries={svcQueue}
                    onSkip={(uuid) => skipTicket(uuid, svc.id)}
                    onFinish={(uuid) => finishTicket(uuid, svc.id)}
                  />
                </div>
              );
            })}
          </>
        ) : shops.length > 0 ? (
          // No services defined — show single default queue
          <>
            <button
              style={{ ...styles.callBtn, width: '100%', padding: '0.85rem', borderRadius: 10, fontSize: '1rem', marginBottom: '1.5rem' }}
              onClick={() => callNext('')}
              disabled={wsStatus !== 'connected'}
            >
              {t('staffQueue.callNext')}
            </button>
            <div style={styles.sectionTitle}>{t('staffQueue.queueLabel')} — {queue.length} {t('staffQueue.waiting')}</div>
            <QueueList entries={queue} onSkip={(uuid) => skipTicket(uuid, '')} onFinish={(uuid) => finishTicket(uuid, '')} />
          </>
        ) : null}
      </main>
    </div>
  );
}
